import { useCallback, useEffect, useState } from 'react';
import { IconMail } from '../../components/Icons';
import { Link, useSearchParams } from 'react-router-dom';
import { toApiError, type ApiError } from '../../api/client';
import { ownerEnquiryApi } from '../../api/endpoints';
import type { Enquiry, PagedResult } from '../../api/types';
import { PageHeader, Pagination } from '../../components/Common';
import { EmptyState, ErrorState, InlineError, TableSkeleton } from '../../components/States';
import { dateTime, plainDate, whatsAppLink } from '../../utils/format';

const STATUSES = ['New', 'Contacted', 'Closed', 'Spam'];

export function OwnerEnquiriesPage() {
  const [params, setParams] = useSearchParams();

  const [data, setData] = useState<PagedResult<Enquiry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const status = params.get('status') ?? undefined;
  const propertyId = params.get('propertyId') ?? undefined;
  const page = Number(params.get('page') ?? 1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setData(await ownerEnquiryApi.list({ status, propertyId, page, pageSize: 20 }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status, propertyId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next);
  };

  const changeStatus = async (enquiry: Enquiry, next: string, notes?: string) => {
    try {
      await ownerEnquiryApi.update(enquiry.id, next, notes ?? enquiry.ownerNotes);
      await load();
    } catch (err) {
      setError(toApiError(err));
    }
  };

  return (
    <>
      <PageHeader
        title="Customer enquiries"
        description="Everyone who has asked about one of your listings."
      />

      <InlineError message={error} />

      <div className="sf-card sf-card-pad mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <span className="small sf-muted">Show:</span>

          <button
            type="button"
            className={`btn btn-sm ${!status ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setFilter('status', undefined)}
          >
            All
          </button>

          {STATUSES.map((value) => (
            <button
              key={value}
              type="button"
              className={`btn btn-sm ${status === value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFilter('status', value)}
            >
              {value}
            </button>
          ))}

          {propertyId && (
            <button type="button" className="btn btn-sm btn-link" onClick={() => setFilter('propertyId', undefined)}>
              Clear property filter
            </button>
          )}
        </div>
      </div>

      {loading && <TableSkeleton rows={5} columns={4} />}
      {!loading && error && <ErrorState error={error} onRetry={load} />}

      {!loading && !error && data && data.items.length === 0 && (
        <EmptyState
          icon={<IconMail />}
          title="No enquiries here"
          description={status ? `No ${status.toLowerCase()} enquiries.` : 'When customers contact you, they appear here.'}
        />
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <>
          <div className="d-flex flex-column gap-2">
            {data.items.map((enquiry) => (
              <div className="sf-card sf-card-pad" key={enquiry.id}>
                <div className="d-flex flex-wrap justify-content-between gap-2">
                  <div className="flex-grow-1">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <strong>{enquiry.name}</strong>
                      <span
                        className={`sf-pill ${
                          enquiry.status === 'New'
                            ? 'sf-pill--info'
                            : enquiry.status === 'Contacted'
                              ? 'sf-pill--ok'
                              : 'sf-pill--muted'
                        }`}
                      >
                        {enquiry.status}
                      </span>
                      <span className="sf-muted small">{dateTime(enquiry.createdAtUtc)}</span>
                    </div>

                    <p className="sf-muted small mb-1">
                      <Link to={`/owner/listings/${enquiry.propertyId}`}>{enquiry.propertyTitle}</Link>
                      {enquiry.roomTypeName && <> · {enquiry.roomTypeName}</>}
                    </p>

                    {enquiry.message && <p className="mb-1">{enquiry.message}</p>}

                    <p className="sf-muted small mb-0">
                      {enquiry.checkInDate && (
                        <>
                          Stay: {plainDate(enquiry.checkInDate)} – {plainDate(enquiry.checkOutDate)}
                          {enquiry.adults && <> · {enquiry.adults} adults</>}
                          {' · '}
                        </>
                      )}
                      {enquiry.moveInDate && <>Move in {plainDate(enquiry.moveInDate)} · </>}
                      {enquiry.phone}
                      {enquiry.email && <> · {enquiry.email}</>}
                    </p>

                    {expanded === enquiry.id && (
                      <OwnerNotes enquiry={enquiry} onSave={(notes) => changeStatus(enquiry, enquiry.status, notes)} />
                    )}
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <a className="btn btn-sm btn-primary" href={`tel:${enquiry.phone}`}>
                      Call
                    </a>
                    <a
                      className="btn btn-sm btn-outline-success"
                      href={whatsAppLink(
                        enquiry.phone,
                        `Hi ${enquiry.name}, thank you for your enquiry about ${enquiry.propertyTitle}.`,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp
                    </a>

                    {enquiry.status === 'New' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => changeStatus(enquiry, 'Contacted')}
                      >
                        Mark contacted
                      </button>
                    )}

                    {enquiry.status !== 'Closed' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => changeStatus(enquiry, 'Closed')}
                      >
                        Close
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn btn-sm btn-link"
                      onClick={() => setExpanded(expanded === enquiry.id ? null : enquiry.id)}
                    >
                      {expanded === enquiry.id ? 'Hide notes' : 'Notes'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Pagination
            result={data}
            onPageChange={(next) => {
              const query = new URLSearchParams(params);
              query.set('page', String(next));
              setParams(query);
            }}
          />
        </>
      )}
    </>
  );
}

function OwnerNotes({ enquiry, onSave }: { enquiry: Enquiry; onSave: (notes: string) => void }) {
  const [notes, setNotes] = useState(enquiry.ownerNotes ?? '');

  return (
    <div className="mt-2">
      <label className="form-label small mb-1" htmlFor={`notes-${enquiry.id}`}>
        Private notes
      </label>
      <textarea
        id={`notes-${enquiry.id}`}
        className="form-control form-control-sm"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={2000}
      />
      <div className="form-text">Only you can see these. The customer never does.</div>
      <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={() => onSave(notes)}>
        Save notes
      </button>
    </div>
  );
}
